import React from 'react';

import { Typography, Progress, Link } from '@useblu/ocean-react';

import Loading from '@/components/Loading';
import Footer from '@/components/Footer';

import { format } from 'date-fns';
import { normalizeMoney } from '@useblu/utils';

import useSuccess from './useSuccess';

import { CheckCircle, XCircle } from '@useblu/ocean-icons-react';
import { IConfig } from './types';
import { cpfCnpjFormater } from '@/utils/formatCpfCnpj';
import * as S from './styles';

const Success: React.FC = () => {
  const { loading, content, getLinkData, linkUrl, handleGetInstallmentValue } =
    useSuccess();

  if (loading) return <Loading />;

  const steps = {
    processing: {
      isPositive: false,
      Icon: <Progress size="sm" />,
      title: 'Pagamento em análise...',
      description: (
        <Typography variant="description" className="description-default">
          Sua compra está sendo verificada.
        </Typography>
      ),
      footer: `Última atualização em ${format(
        new Date(),
        "dd/MM/yyyy 'às' HH:mm",
      )}`,
    },
    confirmed: {
      isPositive: true,
      Icon: <CheckCircle size={18} className="icon" />,
      title: 'Pagamento confirmado',
      footer: 'Em breve, você poderá aproveitar a sua compra.',
    },
    failed: {
      isPositive: false,
      Icon: <XCircle size={18} color="rgba(245, 69, 110, 1)" />,
      title: 'Seu pagamento não foi aprovado',
      description: (
        <Link size="sm" href={linkUrl}>
          Tentar novamente
        </Link>
      ),
      footer: `Última atualização em ${format(
        new Date(),
        "dd/MM/yyyy 'às' HH:mm",
      )}`,
    },
  };

  const currentStep: IConfig = steps[content as keyof typeof steps];

  return (
    <S.Wrapper>
      <S.Container>
        <Typography variant="heading3" className="title">
          Acompanhe sua compra
        </Typography>

        <Typography variant="heading5" className="label">
          Resumo
        </Typography>

        <S.ResumeContainer>
          <S.Resume>
            <Typography variant="description" className="resume-title">
              Valor
            </Typography>
            <Typography variant="heading4" className="resume-value">
              {normalizeMoney(getLinkData?.amount || 0)}
            </Typography>
            <Typography variant="caption" className="resume-caption">
              {
                getLinkData?.last_payment_link_intent?.payment_link_transaction
                  ?.installments
              }
              x sem juros de {normalizeMoney(handleGetInstallmentValue())}
            </Typography>
          </S.Resume>

          <S.Resume>
            <Typography variant="description" className="resume-title">
              Loja
            </Typography>
            <Typography variant="heading4" className="resume-value">
              {getLinkData?.client_name}
            </Typography>
            <Typography variant="caption" className="resume-caption">
              {cpfCnpjFormater(getLinkData?.cpf_cnpj || '')}
            </Typography>
          </S.Resume>
        </S.ResumeContainer>

        <Typography variant="heading5" className="label">
          Status do pagamento
        </Typography>

        <S.StepsContainer>
          <S.Step>
            {currentStep.Icon}

            <S.StepRow>
              <Typography variant="description" className="step-label">
                {currentStep.title}
              </Typography>

              {currentStep?.description && <>{currentStep?.description}</>}
            </S.StepRow>
          </S.Step>

          <S.StepFooter className={currentStep.isPositive ? 'success' : ''}>
            <Typography
              variant="caption"
              className={
                currentStep.isPositive
                  ? 'description-positive'
                  : 'description-default'
              }
            >
              {currentStep.footer}
            </Typography>
          </S.StepFooter>
        </S.StepsContainer>
      </S.Container>
      <S.FooterBox>
        <Footer />
      </S.FooterBox>
    </S.Wrapper>
  );
};

export default Success;
